import type { LightBoxAttachment } from "@/components/ui/lightbox/light-box-attachment";

/**
 * Whether `uri` is served from the media-v3 layout, where every object of an
 * attachment lives under `/<attachmentId>/…` (media/src/v3/lib/keys.ts). The
 * legacy CDN keeps flat keys, so the first path segment tells the two apart
 * without naming a host.
 */
export function isMediaV3Uri(
  uri: string | null | undefined,
  attachmentId: string | null | undefined,
): boolean {
  if (!uri || !attachmentId) return false;
  try {
    return new URL(uri).pathname.startsWith(`/${attachmentId}/`);
  } catch {
    return false;
  }
}

/**
 * The legacy CDN's edge function turns `?download=true` into a
 * `Content-Disposition: attachment` response (devops/serverless/optimized-cdn).
 */
export const getDownloadUrl = (href: string): string => {
  if (!href) {
    return "";
  }
  // A blob: URL is already the fully-resolved bytes (birdie-chat mints these from the
  // authenticated attachment fetch); appending a query yields an unregistered URL, so
  // the download fetch fails. Return it as-is — only media-service URLs take params.
  if (href.startsWith("blob:")) {
    return href;
  }
  const url = new URL(href);
  const params = new URLSearchParams(url.search);
  params.delete("stream");
  params.append("download", "true");
  url.search = params.toString();
  return url.toString();
};

/**
 * The URL the lightbox's download button navigates to. Media-v3 objects sit
 * behind a passive CDN that ignores `?download=true`, so they go through the
 * media service's download route, which answers with a redirect to a
 * short-lived `Content-Disposition: attachment` URL. Legacy objects keep the
 * CDN query; a blob: URL is handed back untouched.
 */
export function resolveDownloadUrl(
  attachment: Pick<
    LightBoxAttachment,
    "id" | "contentUri" | "originalContentUri"
  >,
  options: { mediaServiceUrl: string },
): string {
  const href = attachment.originalContentUri || attachment.contentUri || "";
  if (!href) return "";
  if (isMediaV3Uri(href, attachment.id)) {
    return `${options.mediaServiceUrl}/api/v3/uploads/${attachment.id}/download`;
  }
  return getDownloadUrl(href);
}

/**
 * The still shown before a video plays. Media-v3 writes a real poster
 * (`<id>/<name>_thumbnail.webp`) and stores it as `thumbnailUri`; the legacy
 * CDN renders one on the fly from the video URL with `?variant=medium` — its
 * stored `thumbnailUri` is the same on-the-fly form at a smaller size, so the
 * medium derivation wins there. `null` when no still can exist (a blob: URL).
 */
export function resolveVideoPoster(
  attachment: Pick<
    LightBoxAttachment,
    "contentUri" | "originalContentUri" | "thumbnailUri"
  >,
): string | null {
  const thumbnail = attachment.thumbnailUri ?? "";
  if (thumbnail && !thumbnail.includes("variant=")) return thumbnail;
  const source = attachment.originalContentUri || attachment.contentUri || "";
  if (!source || source.startsWith("blob:")) return null;
  return `${source}?variant=medium`;
}
